import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";

const REFRESH_MS = 60 * 60 * 1000;

// Current UTC time, re-read every hour while the tab is visible, and on return to the tab once an hour or a UTC day has passed.
// `refresh` re-reads it at once, for a user action that asks for "now".
export const useLiveNow = (): { nowUtc: dayjs.Dayjs; refresh: () => void } => {
  const [now, setNow] = useState(() => dayjs.utc());

  useEffect(() => {
    const isStale = () => {
      const current = dayjs.utc();
      return current.diff(now) >= REFRESH_MS || !current.isSame(now, "day");
    };
    const refreshIfStale = () => {
      if (document.visibilityState === "visible" && isStale()) setNow(dayjs.utc());
    };

    const timer = window.setInterval(refreshIfStale, REFRESH_MS);
    document.addEventListener("visibilitychange", refreshIfStale);
    window.addEventListener("focus", refreshIfStale);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshIfStale);
      window.removeEventListener("focus", refreshIfStale);
    };
  }, [now]);

  const refresh = useCallback(() => setNow(dayjs.utc()), []);

  return { nowUtc: now, refresh };
};
